import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { PackagePlus, X } from "lucide-react";

/**
 * Import selected line items from an existing albarán into the inventory
 * catalog (as manual products with source='albaran').
 */
export default function ImportFromAlbaranDialog({ open, onClose, lang, onImported }) {
  const [albaranes, setAlbaranes] = useState([]);
  const [selectedAlb, setSelectedAlb] = useState(null);
  const [checked, setChecked] = useState({});
  const [defaultCategory, setDefaultCategory] = useState("otros");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAlbaranes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/albaranes");
      setAlbaranes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { loadAlbaranes(); setSelectedAlb(null); setChecked({}); setQ(""); } }, [open, loadAlbaranes]);

  const filteredAlb = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return albaranes;
    return albaranes.filter((a) =>
      (a.vendor || "").toLowerCase().includes(s) ||
      (a.albaran_number || "").toLowerCase().includes(s) ||
      (a.date || "").includes(s)
    );
  }, [albaranes, q]);

  const items = selectedAlb?.line_items || [];

  const toggleAll = (val) => {
    const next = {};
    items.forEach((_, i) => { next[i] = val; });
    setChecked(next);
  };

  const doImport = async () => {
    const selected = items
      .map((it, i) => ({ ...it, _i: i }))
      .filter((it) => checked[it._i]);
    if (selected.length === 0) return toast.error(lang === "es" ? "Selecciona al menos una línea" : "Select at least one line");
    setSaving(true);
    try {
      const { data } = await api.post("/inventories/catalog/from-albaran", {
        items: selected.map((it) => ({
          name: it.name,
          category: defaultCategory || "otros",
          subcategory: null,
          unit: it.unit || "ud",
          unit_price: Number(it.unit_price || 0),
        })),
      });
      toast.success(
        lang === "es"
          ? `Importados: ${data.added || 0} nuevos, ${data.updated || 0} actualizados`
          : `Imported: ${data.added || 0} new, ${data.updated || 0} updated`
      );
      onImported?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-albaran-dialog">
        <DialogHeader>
          <DialogTitle>{lang === "es" ? "Añadir desde albarán" : "Add from delivery note"}</DialogTitle>
        </DialogHeader>

        {!selectedAlb ? (
          <>
            <Input placeholder={lang === "es" ? "Buscar por proveedor, nº o fecha…" : "Search by vendor, number or date…"} value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="mt-2 border border-[var(--border-color)] rounded-md max-h-[55vh] overflow-y-auto">
              {loading && <div className="p-4 text-center text-sm text-[var(--text-muted)]">…</div>}
              {!loading && filteredAlb.length === 0 && (
                <div className="p-6 text-center text-sm text-[var(--text-muted)]">
                  {lang === "es" ? "No hay albaranes." : "No delivery notes."}
                </div>
              )}
              {filteredAlb.map((a) => (
                <button
                  key={a.id || a._id}
                  onClick={() => { setSelectedAlb(a); setChecked({}); }}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 border-t border-[var(--border-color)]/40 text-sm hover:bg-[var(--surface-hover)]"
                  data-testid={`alb-pick-${a.id || a._id}`}
                >
                  <span className="font-mono-num text-xs w-24">{a.date || "—"}</span>
                  <span className="flex-1 truncate">{a.vendor || "—"}{a.albaran_number ? ` · ${a.albaran_number}` : ""}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">{(a.line_items || []).length} {lang === "es" ? "líneas" : "lines"}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="text-[var(--text-muted)]">{lang === "es" ? "Albarán" : "Note"}:</span>{" "}
                <span className="font-semibold">{selectedAlb.vendor || "—"}{selectedAlb.albaran_number ? ` · ${selectedAlb.albaran_number}` : ""}</span>{" "}
                <span className="text-[var(--text-muted)]">{selectedAlb.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[10px] uppercase">{lang === "es" ? "Categoría por defecto" : "Default category"}</Label>
                <Input value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)} className="w-32 h-8 text-xs" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => toggleAll(true)}>{lang === "es" ? "Marcar todo" : "Select all"}</Button>
              <Button size="sm" variant="outline" onClick={() => toggleAll(false)}>{lang === "es" ? "Ninguno" : "None"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedAlb(null)}>{lang === "es" ? "← Otro albarán" : "← Other note"}</Button>
            </div>
            <div className="mt-2 border border-[var(--border-color)] rounded-md max-h-[45vh] overflow-y-auto">
              {items.length === 0 && (
                <div className="p-6 text-center text-sm text-[var(--text-muted)]">
                  {lang === "es" ? "Este albarán no tiene líneas." : "This delivery note has no line items."}
                </div>
              )}
              {items.map((it, i) => (
                <label key={`${it.name || "line"}-${i}`} className="flex items-center gap-2 px-3 py-1.5 border-t border-[var(--border-color)]/40 text-xs cursor-pointer hover:bg-[var(--surface-hover)]">
                  <Checkbox checked={!!checked[i]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: !!v }))} data-testid={`alb-line-${i}`} />
                  <span className="flex-1 truncate">{it.name}</span>
                  <span className="w-14 text-right font-mono-num text-[var(--text-muted)]">{it.unit || "ud"}</span>
                  <span className="w-20 text-right font-mono-num">{Number(it.unit_price || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: "always" })}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> {lang === "es" ? "Cancelar" : "Cancel"}</Button>
          {selectedAlb && (
            <Button onClick={doImport} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="alb-import-confirm-btn">
              <PackagePlus size={14} className="mr-1" /> {lang === "es" ? "Importar seleccionados" : "Import selected"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
